"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import React from "react";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { URLs } from "@/app/utils/URLs";
import { UStyles } from "@/app/utils/UStyles";
import styles from "./Button.module.css";

export enum ButtonType {
  PRIMARY = "primary",
  OUTLINE = "outline",
  SOFT = "soft",
  SOFT_GREEN = "softGreen",
  MUTED = "muted",
  OUTLINE_LIGHT = "outlineLight",
  GHOST = "ghost",
  DANGER = "danger",
}

export enum ButtonNativeType {
  SUBMIT = "submit",
  BUTTON = "button",
  RESET = "reset",
}

type Props = Partial<DefaultProps> & {
  text?: string | React.ReactNode;
  href?: string;
  type?: ButtonType;
  onClick?: Function;
  min?: boolean;
  small?: boolean;
  isLink?: boolean;
  loading?: boolean;
  borderMin?: boolean;
  full?: boolean;
  beforeIcon?: React.ReactElement;
  afterIcon?: React.ReactElement;
  external?: boolean;
  download?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
  iconOnly?: boolean;
  selected?: boolean;
  className?: string;
  isResponsiveLink?: boolean;
  prefetch?: boolean;
  nativeType?: ButtonNativeType;
  ariaLabel?: string;
};

export default function Button(props: Props) {
  const {
    text,
    href,
    type = ButtonType.PRIMARY,
    onClick,
    borderMin = false,
    min = true,
    small = false,
    full = false,
    isLink = true,
    prefetch,
    loading = false,
    beforeIcon,
    afterIcon,
    external = false,
    download = false,
    style,
    disabled = false,
    iconOnly = false,
    selected = false,
    className,
    isResponsiveLink = false,
    nativeType = ButtonNativeType.BUTTON,
    ariaLabel,
  } = props;

  const pathname = usePathname();
  const isInactive = disabled || loading;
  const linkHref = isInactive
    ? "#"
    : external
      ? href || "#"
      : URLs.localizeHref(href || "#", pathname);

  const commonClasses = UStyles.classNames(
    styles.button,
    styles[type],
    small ? styles.small : min ? styles.min : "",
    borderMin ? styles.borderMin : "",
    full ? styles.full : "",
    isInactive ? styles.disabled : "",
    iconOnly ? styles.iconOnly : "",
    selected ? styles.selected : "",
    isResponsiveLink ? styles.responsiveLink : "",
    className || "",
  );

  const handleClick = (e: React.MouseEvent) => {
    if (isInactive) {
      e.preventDefault();
      return;
    }
    if (onClick) {
      onClick(e);
    }
  };

  const content = loading ? (
    iconOnly ? (
      <PointsLoader />
    ) : (
      <>
        <span style={{ visibility: "hidden" }}>{text}</span>
        <span className={styles.absoluteLoader}>
          <PointsLoader />
        </span>
      </>
    )
  ) : (
    <>
      {beforeIcon}
      {text}
      {afterIcon}
    </>
  );

  if (isLink) {
    return (
      <Link
        className={commonClasses}
        href={linkHref}
        prefetch={prefetch}
        target={external && !download ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        download={download || undefined}
        style={{ ...style }}
        aria-label={ariaLabel}
        onClick={handleClick}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      className={commonClasses}
      style={{ ...style }}
      type={nativeType}
      disabled={isInactive}
      aria-label={ariaLabel}
      onClick={handleClick}
    >
      {content}
    </button>
  );
}
